import { Image, type ImageName } from '@acme/assets';
import { Button } from 'flowbite-react';
import React from 'react';

export interface ErrorPageProps {
  statusCode: 400 | 401 | 403 | 404 | 500 | 'rates';
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

const errorImages: Record<400 | 401 | 403 | 404 | 500 | 'rates', ImageName> = {
  400: '400-error-bad-request',
  401: '401-error-unauthorized',
  403: '403-error-forbidden',
  404: '404-error-with-a-landscape',
  500: '500-internal-server-error',
  rates: 'profiling',
};

const errorConfig = {
  400: {
    defaultTitle: 'Извините, что-то пошло не так.',
    defaultDescription: '',
    defaultActionLabel: 'На главную',
  },
  401: {
    defaultTitle: 'У вас нет доступа к этому разделу',
    defaultDescription:
      'Если вы считаете, что это ошибка — обратитесь в поддержку',
    defaultActionLabel: '',
  },
  403: {
    defaultTitle: 'У вас нет доступа к этому разделу',
    defaultDescription:
      'Если вы считаете, что это ошибка — обратитесь в поддержку',
    defaultActionLabel: '',
  },
  404: {
    defaultTitle: 'Страница не найдена',
    defaultDescription:
      'Возможно, она была удалена или вы допустили ошибку в адресе',
    defaultActionLabel: '',
  },
  500: {
    defaultTitle: 'Произошла непредвиденная ошибка',
    defaultDescription: 'Мы уже работаем над её устранением',
    defaultActionLabel: 'Обновить страницу',
  },
  rates: {
    defaultTitle: 'Не удалось загрузить курсы валют',
    defaultDescription: 'Попробуйте обновить страницу',
    defaultActionLabel: 'Перезагрузить страницу',
  },
};

export const ErrorPage: React.FC<ErrorPageProps> = ({
  statusCode,
  title,
  description,
  actionLabel,
  onAction,
  className = '',
}) => {
  const config = errorConfig[statusCode];
  const imageName = errorImages[statusCode];

  const handleAction = () => {
    if (onAction) {
      onAction();
    } else if (statusCode === 500) {
      window.location.reload();
    }
  };

  return (
    <div
      className={`flex flex-col items-center justify-center px-0 py-24 ${className}`}
    >
      <div className="bg-white flex flex-col gap-6 items-center justify-center p-6 rounded-lg shadow-md w-[448px]">
        <div className="overflow-hidden relative shrink-0 w-40">
          <Image
            name={imageName}
            alt={`Error ${statusCode}`}
            className="w-full h-full object-contain"
            width="100%"
            height="100%"
          />
        </div>
        <div className="flex flex-col gap-1 items-center relative shrink-0 w-full">
          <div className="flex flex-col gap-0 items-center relative shrink-0 w-full text-base text-center">
            <div className="flex flex-col font-medium justify-center leading-normal relative shrink-0 text-gray-900 w-[348px]">
              <p className="leading-[1.5] whitespace-pre-wrap">
                {title || config.defaultTitle}
              </p>
            </div>
            {description || config.defaultDescription ? (
              <p className="font-normal leading-[1.5] relative shrink-0 text-gray-500 w-full whitespace-pre-wrap mt-1">
                {description || config.defaultDescription}
              </p>
            ) : null}
          </div>
        </div>
        {(onAction || statusCode === 400 || statusCode === 500 || statusCode === 'rates') && (
          <Button
            color="purple"
            onClick={handleAction}
            className="bg-purple-900 hover:bg-purple-800"
          >
            {actionLabel || config.defaultActionLabel}
          </Button>
        )}
      </div>
    </div>
  );
};
